import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

const CATEGORIES = [
  { key: "deliverables", name: "Deliverables", weight: 40 },
  { key: "skills", name: "Skills", weight: 30 },
  { key: "culture", name: "Culture", weight: 30 },
];

const levels = {
  1: { label: "Poor", color: "bg-red-500/30 border-red-500 text-red-600" },
  2: { label: "Basic", color: "bg-yellow-500/30 border-yellow-500 text-yellow-600" },
  3: { label: "Intermediate", color: "bg-blue-500/30 border-blue-500 text-blue-600" },
  4: { label: "Advanced", color: "bg-teal-500/30 border-teal-500 text-teal-600" },
  5: { label: "Exceptional", color: "bg-purple-500/30 border-purple-500 text-purple-600" },
};

function TalentMatrix({ team, navigate }) {
  const performanceLevels = [
    { level: 5, label: "Exceptional", color: "bg-purple-100", textColor: "text-purple-700" },
    { level: 4, label: "Advanced", color: "bg-teal-100", textColor: "text-teal-700" },
    { level: 3, label: "Intermediate", color: "bg-blue-100", textColor: "text-blue-700" },
    { level: 2, label: "Basic", color: "bg-yellow-100", textColor: "text-yellow-700" },
    { level: 1, label: "Poor", color: "bg-red-100", textColor: "text-red-700" },
  ];

  const talentBarLevel = 3;

  function getLevel(score) {
    if (score >= 4.5) return 5;
    if (score >= 3.5) return 4;
    if (score >= 2.5) return 3;
    if (score >= 1.5) return 2;
    return 1;
  }

  function getPeopleAtLevel(categoryKey, level) {
    return team.filter((m) => {
      if (!m.isAssessed) return false;
      const avg = m[categoryKey]?.average || 0;
      return getLevel(avg) === level;
    });
  }

  function getCategory(member) {
    if (member.overall >= 4) return "aPlayer";
    if (member.overall >= 3) return "aboveBar";
    return "underperformer";
  }

  const categoryLabels = [
    { key: "aPlayer", label: "A-Player", color: "bg-gradient-to-r from-purple-600 to-teal-600" },
    { key: "aboveBar", label: "Above Bar", color: "bg-blue-600" },
    { key: "underperformer", label: "Underperformer", color: "bg-gray-600" },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Talent Matrix</h3>

      <div className="flex gap-6">
        <div className="flex flex-col justify-between py-2" style={{ height: "400px" }}>
          {categoryLabels.map((cat) => (
            <div key={cat.key} className="flex items-center gap-2">
              <div className={`px-3 py-1.5 rounded-lg text-xs font-medium text-white ${cat.color}`}>{cat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex-1">
          <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: `auto repeat(${CATEGORIES.length}, 1fr)` }}>
            <div></div>
            {CATEGORIES.map((cat) => (
              <div key={cat.key} className="text-center text-sm font-medium text-gray-600 py-2">{cat.name}</div>
            ))}
          </div>

          {performanceLevels.map((levelData) => (
            <div key={levelData.level} className="grid gap-1 mb-1" style={{ gridTemplateColumns: `auto repeat(${CATEGORIES.length}, 1fr)` }}>
              <div className={`flex items-center px-3 py-3 rounded-l-lg ${levelData.color}`}>
                <span className={`text-xs font-medium ${levelData.textColor}`}>{levelData.label}</span>
              </div>
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.key}
                  className={`${levelData.color} p-2 min-h-[60px] flex flex-wrap gap-1 items-center justify-center ${levelData.level === talentBarLevel ? "border-b-2 border-white/50" : ""}`}>
                  {getPeopleAtLevel(cat.key, levelData.level).map((person) => (
                    <button
                      key={person.id + "-" + cat.key}
                      onClick={() => navigate(`/performance/assess/${person.id}`)}
                      className="w-8 h-8 rounded-full overflow-hidden hover:ring-2 hover:ring-teal-400 transition-all"
                      title={person.name}>
                      {person.avatar ? (
                        <img src={person.avatar} alt={person.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-400 flex items-center justify-center text-white text-xs font-medium">
                          {person.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="w-64 space-y-4 text-sm">
          {team
            .filter((m) => m.isAssessed)
            .slice(0, 3)
            .map((person) => {
              const cat = getCategory(person);
              return (
                <div key={person.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    {person.avatar ? (
                      <img src={person.avatar} alt={person.name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-medium">
                        {person.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                    )}
                    <span className="font-medium text-gray-900">{person.name?.split(" ")[0]}</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    {cat === "aPlayer" && "is an A-player as they significantly beat expectations."}
                    {cat === "aboveBar" && "is above bar in most dimensions."}
                    {cat === "underperformer" && "is struggling to meet expectations for their role."}
                  </p>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default function TeamGrid({ cycle }) {
  const navigate = useNavigate();
  const { businessUnit } = useSelector((state) => state.Auth);
  const business_unit_id = businessUnit?._id;
  const quarter = cycle?.quarter;
  const year = cycle?.year;
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [viewMode, setViewMode] = useState("table");

  useEffect(() => {
    if (!business_unit_id || !quarter || !year) {
      setTeam([]);
      return;
    }
    fetchTeamReviews();
  }, [business_unit_id, quarter, year]);

  async function fetchTeamReviews() {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/performance_review/search", { business_unit_id, quarter, year });
      if (!ok) return toast.error("Failed to load team");

      const mapped = data.map((r) => ({
        id: r._id,
        name: r.user_name,
        avatar: r.user_avatar,
        seniority: r.seniority,
        isAssessed: r.overall_score > 0 || r.status === "completed",
        deliverables: r.deliverables,
        skills: r.skills,
        culture: r.culture,
        overall: r.overall_score || 0,
      }));

      setTeam(mapped);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }

  function getScoreColor(score) {
    if (score >= 4) return "bg-teal-500/40 text-teal-700";
    if (score >= 3) return "bg-blue-500/40 text-blue-700";
    if (score >= 2) return "bg-yellow-500/40 text-yellow-700";
    return "bg-red-500/40 text-red-700";
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const visibleCategories = selectedCategory === "all" ? CATEGORIES : CATEGORIES.filter((c) => c.key === selectedCategory);

  const sortedTeam = [...team].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "overall") return b.overall - a.overall;
    if (sortBy === "seniority") {
      const order = { Lead: 0, Senior: 1, Mid: 2, Junior: 3 };
      return order[a.seniority] - order[b.seniority];
    }
    return 0;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("table")}
              className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${viewMode === "table" ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
              </svg>
              Table
            </button>
            <button
              onClick={() => setViewMode("matrix")}
              className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${viewMode === "matrix" ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              Matrix
            </button>
          </div>

          {viewMode === "table" && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${selectedCategory === "all" ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"}`}>
                All
              </button>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`px-4 py-2 text-sm rounded-md transition-colors ${selectedCategory === cat.key ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"}`}>
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {viewMode === "table" && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700">
              <option value="name">Name</option>
              <option value="overall">Overall Score</option>
              <option value="seniority">Seniority</option>
            </select>
          </div>
        )}
      </div>

      {viewMode === "matrix" ? (
        <TalentMatrix team={team} navigate={navigate} />
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">Developer</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Seniority</th>
                  {visibleCategories.map((cat) => (
                    <th key={cat.key} className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                      {cat.name} ({cat.weight}%)
                    </th>
                  ))}
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Overall</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedTeam.map((member) => (
                  <tr
                    key={member.id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${!member.isAssessed ? "opacity-50" : ""}`}
                    onClick={() => navigate(`/performance/assess/${member.id}`)}>
                    <td className="px-4 py-3 sticky left-0 bg-white">
                      <div className="flex items-center gap-3">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.name} className={`w-8 h-8 rounded-full object-cover ${!member.isAssessed ? "grayscale opacity-60" : ""}`} />
                        ) : (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${member.isAssessed ? "bg-gradient-to-br from-blue-500 to-blue-600" : "bg-gray-400"}`}>
                            {member.name.charAt(0)}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900">{member.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs text-gray-600 px-2 py-1 bg-gray-100 rounded">{member.seniority}</span>
                    </td>
                    {member.isAssessed ? (
                      <>
                        {visibleCategories.map((cat) => {
                          const avg = member[cat.key]?.average || 0;
                          return (
                            <td key={cat.key} className="px-2 py-3 text-center">
                              <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${getScoreColor(avg)}`}>
                                {avg.toFixed(1)}
                              </span>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-lg text-sm font-bold ${getScoreColor(member.overall)}`}>
                            {member.overall.toFixed(1)}
                          </span>
                        </td>
                      </>
                    ) : (
                      <td colSpan={visibleCategories.length + 1} className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                          <span>📋</span>
                          Not assessed yet
                          <span className="text-blue-600 text-xs ml-2">Click to assess →</span>
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            {CATEGORIES.map((cat) => {
              const assessedTeam = team.filter((m) => m.isAssessed);
              const avgScore =
                assessedTeam.length > 0
                  ? (assessedTeam.reduce((sum, m) => sum + (m[cat.key]?.average || 0), 0) / assessedTeam.length).toFixed(1)
                  : "—";
              return (
                <div key={cat.key} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <h4 className="text-sm text-gray-600 mb-1">{cat.name} Avg</h4>
                  <p className="text-2xl font-bold text-gray-900">{avgScore}</p>
                  <p className="text-xs text-gray-500 mt-1">Weight: {cat.weight}%</p>
                </div>
              );
            })}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <h4 className="text-sm text-gray-600 mb-1">Overall Avg</h4>
              <p className="text-2xl font-bold text-blue-600">
                {(() => {
                  const assessed = team.filter((m) => m.isAssessed);
                  return assessed.length > 0 ? (assessed.reduce((sum, m) => sum + m.overall, 0) / assessed.length).toFixed(1) : "—";
                })()}
              </p>
              <p className="text-xs text-gray-500 mt-1">{team.filter((m) => m.isAssessed).length}/{team.length} assessed</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
